import { create } from 'zustand';
import type { ChannelPayloadMap } from '@/realtime/types';

/**
 * 实时行情 store — 由 WebSocket `quote` 频道推送驱动(每帧 bid/ask)。
 *
 * 设计同 App 端 quote_controller 的 quoteCacheProvider:维护 symbolId → 最新行情,
 * 组件用 selector 只订阅自己关心的品种(随 tick rerender)。
 * 持仓现价 / 浮动盈亏由前端用这里的行情本地计算(cTrader 官方 App 也是客户端逐 tick 算)。
 */
export interface LiveQuote {
  symbolId: string;
  bid: number;
  ask: number;
  bidDigits: number;
  askDigits: number;
  ts: number;
  /** 上一帧价格,用于涨/跌方向着色与闪烁动画;首帧为 undefined */
  prevBid?: number;
  prevAsk?: number;
}

interface QuoteState {
  quotes: Record<string, LiveQuote>;
  applyQuote: (p: ChannelPayloadMap['quote']) => void;
  clear: () => void;
}

export const useQuoteStore = create<QuoteState>()((set) => ({
  quotes: {},
  applyQuote: (p) =>
    set((state) => {
      // symbolId 兼容字符串/数字:后端契约规定 ID 用 string,但 quote 频道历史上误发过数字
      // (见 app/lib/api/ws/ws_events.dart 同款兼容注释)。统一成字符串避免漏帧。
      const symbolId = String(p.symbolId);
      const prev = state.quotes[symbolId];
      return {
        quotes: {
          ...state.quotes,
          [symbolId]: {
            symbolId,
            bid: p.bid,
            ask: p.ask,
            bidDigits: p.bidDigits ?? 2,
            askDigits: p.askDigits ?? 2,
            ts: p.ts,
            prevBid: prev?.bid,
            prevAsk: prev?.ask,
          },
        },
      };
    }),
  clear: () => set({ quotes: {} }),
}));
